import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useSubscription } from '../contexts/SubscriptionContext';
import OnboardingQuiz from '../components/OnboardingQuiz';

type User = {
  id: string;
  email: string;
  name: string;
  picture: string;
  objective?: string;
  subscription?: string;
  subscribed_at?: string;
  subscribed_until?: string;
  unsubscribed_at?: string;
  has_access?: boolean;
  trial_end?: string;
  trial_active?: boolean;
  subscription_active?: boolean;
  role?: string;
  unlocked_modes?: string[];
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => void;
  setUserObjective: (objective: string) => Promise<void>;
  updateUserState: () => Promise<void>;
  setIsPolling: (value: boolean) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showObjectiveQuiz, setShowObjectiveQuiz] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const navigate = useNavigate();

  const updateUserState = async () => {
    try {
      const profile = await api.getUserProfile();
      const { experience, level } = await api.getUserProgress();
      setUser({
        id: profile.id,
        email: profile.email,
        name: profile.name,
        picture: profile.picture,
        objective: profile.objective,
        experience,
        level,
        unlocked_modes: profile.unlocked_modes,
        subscription: profile.subscription,
        subscribed_at: profile.subscribed_at,
        subscribed_until: profile.subscribed_until,
        unsubscribed_at: profile.unsubscribed_at,
        role: profile.role,
        has_access: profile.has_access,
        trial_end: profile.trial_end,
        trial_active: profile.trial_active,
        subscription_active: profile.subscription_active,
      });
    } catch (error) {
      console.error('Error getting user profile:', error);
      setUser(null);
    }
  };

  const setUserObjective = async (objective: string) => {
    if (user) {
      try {
        await api.setUserObjective(objective);
        setUser({ ...user, objective });
        setShowObjectiveQuiz(false);
        navigate('/dashboard');
      } catch (error) {
        console.error('Error setting user objective:', error);
        throw error;
      }
    }
  };

  // Poll for invoice status
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    if (isPolling) {
      pollInterval = setInterval(async () => {
        try {
          const { paid } = await api.checkInvoiceStatus();
          if (paid) {
            setIsPolling(false);
            // Update user state and wait for it to complete
            await updateUserState();
            // Only navigate after user state is updated
            navigate('/dashboard?subscription=success&upgrade=true', { replace: true });
          }
        } catch (error) {
          console.error('Error checking invoice status:', error);
        }
      }, 2000);

      // Clean up interval on unmount or when polling stops
      return () => clearInterval(pollInterval);
    }
  }, [isPolling, navigate]);

  useEffect(() => {
    updateUserState().finally(() => setIsLoading(false));
  }, []);

  const signInWithGoogle = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI;
    let authWindow: Window | null = null;

    return new Promise<void>((resolve, reject) => {
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Listen for the message from the popup
      const handleMessage = async (event: MessageEvent) => {
        if (event.data === 'success') {
          window.removeEventListener('message', handleMessage);
          if (authWindow) {
            authWindow.close();
          }

          try {
            const isFirstConnection = await api.checkFirstConnection();
            await updateUserState();

            if (isFirstConnection) {
              setShowObjectiveQuiz(true);
            } else {
              navigate('/dashboard');
            }

            resolve();
          } catch (error) {
            console.error('Error getting user profile:', error);
            reject(error);
          }
        }
      };

      window.addEventListener('message', handleMessage);

      authWindow = window.open(
        `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `scope=email profile&` +
        `access_type=offline&` +
        `prompt=consent`,
        'Google Sign In',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Check if popup was blocked
      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        reject(new Error('Popup was blocked'));
        return;
      }

      // Check if popup is closed
      const checkClosed = setInterval(() => {
        if (!authWindow || authWindow.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          reject(new Error('Popup closed by user'));
        }
      }, 100);
    });
  };

  const signOut = async () => {
    try {
      // Call backend to clear the auth cookie
      await fetch(`${import.meta.env.VITE_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
    setUser(null);
  };

  // Update user state whenever isPolling changes
  useEffect(() => {
    if (isPolling) {
      const pollInterval = setInterval(async () => {
        try {
          const { paid } = await api.checkInvoiceStatus();
          if (paid) {
            setIsPolling(false);
            await updateUserState();
            navigate('/dashboard?subscription=success&upgrade=true', { replace: true });
          }
        } catch (error) {
          console.error('Error checking invoice status:', error);
        }
      }, 2000);

      return () => clearInterval(pollInterval);
    }
  }, [isPolling, navigate]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      isLoading,
      signInWithGoogle,
      signOut,
      setUserObjective,
      updateUserState,
      setIsPolling
    }}>
      {showObjectiveQuiz && user && !user.objective && (
        <OnboardingQuiz onComplete={setUserObjective} />
      )}
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext }